<template>
  <tr
    v-if="bluredOrNotHidden"
    :class="{ active: isActive, hovered: showActions }"
  >
    <td
      :class="{
        'block-borders': showDragBorder,
        'dragged-opacity': isCurrentElementDragged,
      }"
      class="draggableBlock"
      data-block="Loop"
      @mouseover="mouseOver"
      @mouseleave="mouseOut"
      @click.stop="() => click()"
    >
      <blockActions
        v-if="showActions"
        :column-padding-x="columnPaddingX"
        :column-width="columnWidth"
        :disable-up-movement="disableUpMovement"
        :disable-down-movement="disableDownMovement"
        :block-height="blockHeight"
        :block-width="blockWidth"
        :is-active="isActive"
        :is-last-column="isLastColumn"
        :section-index="sectionIndex"
        :column-index="columnIndex"
        :block-index="blockIndex"
        :product="true"
        :click="click"
        :show-paste="showPaste"
        :copied="copied"
        @duplicate="copyBlock"
        @remove="deleteBlock"
        @moveUp="moveUp"
        @moveDown="moveDown"
        @copy="copyToClipboard"
      ></blockActions>
      <div :id="id" :style="{ ...hiddenStyle, ...style }">
        <table :id="id" width="100%" style="border-spacing: 0px">
          <tr>
            <td
              style="
                text-align: center;
                vertical-align: top;
                direction: ltr;
                font-size: 0px;
              "
            >
              <div
                v-if="!forMergetag"
                class="relative grid h-20 w-full cursor-pointer justify-center bg-[#e7f3ff] text-center font-inter leading-[18px] text-[#6da8ff] outline outline-[#6da8ff4d] hover:bg-[#cee7ff] hover:text-[#4883ff]"
                style="font-size: 13px"
              >
                <div class="pointer-events-none m-auto">
                  <div class="img-text">
                    {{ $t("mjml.select_mergetag") }}
                  </div>
                </div>
              </div>
              <div v-else-if="layout == 'column'">
                <div
                  v-for="index in maxPerRow"
                  :key="index"
                  data-testid="loop-column"
                  style="
                    display: inline-block;
                    direction: ltr;
                    font-size: 13px;
                    text-align: left;
                    box-sizing: border-box;
                  "
                  :style="{
                    ...getColumnStyle(block.children[0]),
                    width: `${getColumnWidth(100 / maxPerRow + '%')}`,
                  }"
                >
                  <table
                    width="100%"
                    style="border-spacing: 0px"
                    :style="
                      block.children[0].attributes['inner-attributes']
                        ? getTabelStyle(block.children[0])
                        : {}
                    "
                  >
                    <tbody>
                      <MjLoopBlock
                        v-for="(element, elIndex) in block.children[0].children"
                        :key="element.uid"
                        :block="element"
                        :section-index="sectionIndex"
                        :column-index="columnIndex"
                        :block-index="blockIndex"
                        :loop-column-index="index"
                        :loop-block-index="elIndex"
                        :dragging="isDragging"
                        @click="openOptions(element.uid)"
                      />
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else>
                <div
                  v-for="(column, index) in block.children"
                  :key="index"
                  data-testid="loop-column"
                  :style="getColumnStyle(column)"
                  style="
                    display: inline-block;
                    direction: ltr;
                    font-size: 13px;
                    text-align: left;
                    box-sizing: border-box;
                  "
                >
                  <table
                    width="100%"
                    style="border-spacing: 0px"
                    :style="
                      column.attributes['inner-attributes']
                        ? getTabelStyle(column)
                        : {}
                    "
                  >
                    <tbody>
                      <VueDraggable
                        v-if="column.children.length > 0"
                        :model-value="column.children"
                        :animation="150"
                        :group="{
                          name: `loopblocks_${block.uid}`,
                          pull: 'clone',
                        }"
                        @update="onUpdate($event, index)"
                        @add="onAdd($event, column, index)"
                        @start="onStart($event, index)"
                        @end="onEnd"
                      >
                        <MjLoopBlock
                          v-for="(element, elIndex) in column.children"
                          :key="element.uid"
                          :block="element"
                          :section-index="sectionIndex"
                          :column-index="columnIndex"
                          :block-index="blockIndex"
                          :loop-column-index="index"
                          :loop-block-index="elIndex"
                          :dragging="isDragging"
                          @click="openOptions(element.uid)"
                        />
                      </VueDraggable>

                      <div
                        v-else
                        class="relative grid min-h-20 w-full cursor-pointer justify-center bg-[#e7f3ff] text-center font-inter leading-[18px] text-[#6da8ff] outline outline-[#6da8ff4d] hover:bg-[#cee7ff] hover:text-[#4883ff]"
                      >
                        <VueDraggable
                          :model-value="column.children"
                          :animation="150"
                          :group="{
                            name: `loopblocks_${block.uid}`,
                            pull: 'clone',
                          }"
                          @update="onUpdate($event, index)"
                          @add="onAdd($event, column, index)"
                          @start="onStart($event, index)"
                          @end="onEnd"
                        >
                          <div class="pointer-events-none mb-8 mt-8">
                            <div class="img-text">
                              {{ $t("mjml.drag_children") }}
                            </div>
                          </div>
                        </VueDraggable>
                      </div>
                    </tbody>
                  </table>
                </div>
              </div>
            </td>
          </tr>
        </table>
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import blockProperties from "@/composables/useBlockProperties";
  import blockActions from "./blockActions.vue";
  import MjLoopBlock from "./loopBlock/mjLoopBlock.vue";
  import { TopolLoopBlock } from "@/schemas/template/blocks/loopBlockSchema";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { VueDraggable } from "vue-draggable-plus";
  import { computed, ref } from "vue";

  const props = defineProps<{
    block: TopolLoopBlock;
    columnIndex: number;
    sectionIndex: number;
    blockIndex: number;
    isLocked: boolean;
  }>();

  const {
    bluredOrNotHidden,
    showActions,
    showPaste,
    blockHeight,
    blockWidth,
    hiddenStyle,
    id,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isCurrentElementDragged,
    showDragBorder,
    isLastColumn,
    copied,
    mouseOver,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  } = blockProperties(props);

  const mjmlStore = useMjmlStore();

  const oldColumnIndex = ref<number | null>(null);
  const oldChildBlockIndex = ref<number | null>(null);
  const isDragging = ref(false);
  const columnPotentialInnerAttributes = [
    "border-radius",
    "border",
    "background-color",
    "border-top",
    "border-bottom",
    "border-left",
    "border-right",
    "column-gradient",
  ];

  const forMergetag = computed(() => {
    return props.block.forMergeTag;
  });

  const layout = computed(() => {
    return props.block.layout || "row";
  });

  const maxPerRow = computed(() => {
    return props.block.maxPerRow || 1;
  });

  const style = computed(() => {
    const noStyleAttr = ["containerWidth", "css-class"];
    const style: any = {};
    for (const attr in props.block.attributes) {
      if (!noStyleAttr.includes(attr)) {
        if (attr === "gradient") {
          style["background-image"] =
            props.block.attributes[attr as keyof typeof props.block.attributes];
          continue;
        }
        style[attr] =
          props.block.attributes[attr as keyof typeof props.block.attributes];
      }
    }
    return style;
  });

  function getColumnWidth(width: string) {
    if (mjmlStore.isMobileFirstEnabled) {
      const doNotStackOnMobile = props.block.attributes["in-group"];

      if (mjmlStore.isMobileView && !doNotStackOnMobile) {
        return "100%";
      }
    }

    return width || "100%";
  }

  function getTabelStyle(column: TopolLoopBlock["children"][0]) {
    const style: any = {};
    for (const attr in column.attributes) {
      if (columnPotentialInnerAttributes.includes(attr)) {
        if (attr === "column-gradient") {
          style["background-image"] =
            column.attributes[attr as keyof typeof column.attributes];
          continue;
        }
        if (attr === "width") {
          style["width"] = getColumnWidth(
            column.attributes[attr as keyof typeof column.attributes] as string
          );
          continue;
        }
        style[attr] = column.attributes[attr as keyof typeof column.attributes];
      }
    }
    return style;
  }

  function getColumnStyle(column: TopolLoopBlock["children"][0]) {
    const style: any = {};
    if (column.attributes["inner-attributes"]) {
      for (const attr in column.attributes) {
        if (
          !columnPotentialInnerAttributes.includes(attr) &&
          attr !== "css-class" &&
          attr !== "inner-attributes"
        ) {
          if (attr === "width") {
            style["width"] = getColumnWidth(
              column.attributes[
                attr as keyof typeof column.attributes
              ] as string
            );
            continue;
          }
          style[attr] =
            column.attributes[attr as keyof typeof column.attributes];
        }
      }
    } else {
      for (const attr in column.attributes) {
        if (attr !== "css-class" && attr !== "inner-attributes") {
          if (attr === "column-gradient") {
            style["background-image"] =
              column.attributes[attr as keyof typeof column.attributes];
            continue;
          }
          if (attr === "width") {
            style["width"] = getColumnWidth(
              column.attributes[
                attr as keyof typeof column.attributes
              ] as string
            );
            continue;
          }
          style[attr] =
            column.attributes[attr as keyof typeof column.attributes];
        }
      }
    }
    return style;
  }

  function onStart(evt: any, columnIndex: number) {
    isDragging.value = true;
    // Save starting column and block for later reordering
    oldColumnIndex.value = columnIndex;
  }

  function onEnd() {
    oldColumnIndex.value = null;
    oldChildBlockIndex.value = null;
    isDragging.value = false;
  }

  function onAdd(
    evt: any,
    column: TopolLoopBlock["children"][0],
    columnIndex: number
  ) {
    const index =
      evt.newIndex >= column.children.length
        ? column.children.length - 1
        : evt.newIndex;
    // column.children.splice(index, 1);

    if (oldColumnIndex.value !== null) {
      mjmlStore.reorderLoopBlocksBetweenColumns({
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
        oldLoopColumnIndex: oldColumnIndex.value,
        oldIndex: evt.oldIndex,
        loopColumnIndex: columnIndex,
        newIndex: index,
      });
    }
  }

  function onUpdate(evt: any, columnIndex: number) {
    mjmlStore.reorderLoopBlockChildren({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
      loopColumnIndex: columnIndex,
      oldIndex: evt.oldIndex,
      newIndex: evt.newIndex,
    });
  }

  function openOptions(uid: string) {
    let timeoutTime = 400;
    if (
      document.querySelector(
        `[data-testid="child-block-${uid}"] [data-testid="t-tab-toggle-open"] button`
      )
    ) {
      timeoutTime = 10;
    }
    setTimeout(() => {
      const openSetting: HTMLElement = document.querySelector(
        `[data-testid="child-block-${uid}"] [data-testid="t-tab-toggle-open"] button`
      ) as HTMLElement;

      if (openSetting && !openSetting.classList.contains("!outline")) {
        openSetting.click();
      }

      if (openSetting) {
        setTimeout(() => {
          (document.querySelector(".scroll-area") as HTMLElement).scrollTop =
            openSetting.offsetTop - openSetting.offsetHeight;
        }, 100);
      }
    }, timeoutTime);
  }
</script>

<style lang="scss" scoped>
  .placeholder-container {
    color: rgb(109, 168, 255);
    background-color: rgb(231, 243, 255);
    text-decoration: none;
    text-align: center;
    line-height: 18px;
    display: flex;
    position: relative;
    justify-content: center;
    cursor: pointer;
    // border-radius: 6px;
    border: none;
    outline: rgba(109, 168, 255, 0.3) 1px solid;

    width: calc(100%);
    height: 140px;
    display: flex;
    justify-content: center;
    font: var(
      --chip-font,
      var(--chip-font-weight, var(--font-normal))
        var(--chip-font-size, var(--font-xs)) / var(--chip-font-line-height, 1)
        var(--chip-font-family, var(--font-family-primary))
    );

    .img-text-container {
      margin-top: 38px;
      pointer-events: none;

      .img-1 {
        padding-bottom: 12px;
      }

      .img-2 {
        padding-bottom: 12px;
        display: none;
      }
    }
  }

  .placeholder-container:hover,
  .placeholder-container-hovered {
    background-color: rgb(206, 231, 255) !important;
    color: rgb(72, 131, 255) !important;
    border-radius: 0px;
    .img-text-container {
      .img-1 {
        padding-bottom: 12px;
        display: none;
      }

      .img-2 {
        padding-bottom: 12px;
        display: inline-block;
      }
    }
  }
</style>
